import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import classNames from "classnames";
import debounce from "lodash-es/debounce";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";

import { CrossIcon, SearchIcon } from "components/Icons";

import styles from "./SearchInput.module.scss";

interface Props {
  defaultValue: string;
  onChange: (value: string) => void;
  handleKeyDown?: (event: React.KeyboardEvent) => void;
  placeholder: string;
  testId: string;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  iconClassName?: string;
  autoFocus?: boolean;
  debounceMs?: number;
  autoComplete?: string;
}

const stopPropagation = (event: React.KeyboardEvent) => event.stopPropagation();

const SearchInput: React.FC<Props> = props => {
  const {
    defaultValue,
    placeholder,
    disabled = false,
    className,
    inputClassName,
    autoFocus = false,
    testId,
    handleKeyDown,
    onChange,
    debounceMs,
    iconClassName,
    autoComplete = "off",
  } = props;

  const inputElement = useRef<HTMLInputElement | null>(null);

  const dependencies = useRef({
    onChange,
  });

  useLayoutEffect(() => {
    dependencies.current = {
      onChange,
    };
  });

  const reset = useCallback(() => {
    const { onChange } = dependencies.current;
    if (inputElement.current) {
      inputElement.current.value = "";
    }
    onChange("");
  }, []);

  useEffect(() => {
    if (defaultValue === "") {
      reset();
    }
  }, [defaultValue, reset]);

  const debouncedOnChange = useMemo(() => {
    const { onChange } = dependencies.current;
    return debounce(onChange, debounceMs);
  }, [debounceMs]);

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> =
    useCallback(
      event => {
        const { onChange } = dependencies.current;
        const value = event.target.value;
        debounceMs ? debouncedOnChange(value) : onChange(value);
      },
      [debounceMs, debouncedOnChange]
    );

  return (
    <TextField
      inputRef={inputElement}
      disabled={disabled}
      classes={{
        root: classNames(styles.root, className),
      }}
      autoFocus={autoFocus}
      defaultValue={defaultValue}
      variant="outlined"
      size="small"
      placeholder={placeholder}
      autoComplete={autoComplete}
      onChange={handleInputChange}
      slotProps={{
        input: {
          classes: {
            root: classNames(styles.inputContainer, inputClassName),
            focused: styles.inputFocused,
          },
          endAdornment: (
            <InputAdornment
              classes={{
                root: classNames(styles.iconContainer, iconClassName),
              }}
              position="end"
              disablePointerEvents={!inputElement.current?.value}
              onClick={reset}
            >
              {inputElement.current?.value ? <CrossIcon /> : <SearchIcon />}
            </InputAdornment>
          ),
        },
        htmlInput: {
          "data-testid": testId,
        },
      }}
      onKeyDown={handleKeyDown || stopPropagation}
    />
  );
};

export default SearchInput;
